// studentapps/StudentApp.js
import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Container,
  Typography,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { AuthProvider, useAuth } from './AuthContext';
import Navigation from './Navigation';
import Dashboard from './Dashboard';
import ProblemSolving from './ProblemSolving';
import TodoApp from './TodoApp';
import NotesApp from './NotesApp';
import StudyTimer from './StudyTimer';
import ResourceLibrary from './ResourceLibrary';
import Login from './Login';
import Footer from './Footer';

// Modern theme with custom color scheme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0a1128',
      light: '#1a1f3d',
      dark: '#050a1a',
    },
    secondary: {
      main: '#8b0000',
      light: '#a83232',
      dark: '#620000',
    },
    accent: {
      main: '#ff4d4d',
      light: '#ff7979',
      dark: '#cc0000',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0a1128',
      secondary: '#4a5568',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.125rem',
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #050a1a 0%, #0a1128 100%)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #8b0000 0%, #a83232 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #620000 0%, #8b0000 100%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
        },
      },
    },
  },
});

const MainContent = styled(Box)(({ theme }) => ({
  marginLeft: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
}));

// Header component for Student App
const StudentHeader = ({ title, onMenuToggle, user, onSignOut }) => {
  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)',
        color: 'white',
        padding: '16px 24px',
        borderRadius: 2,
        marginBottom: 3,
        boxShadow: '0 4px 20px rgba(10, 17, 40, 0.15)',
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight="700" sx={{ color: 'white' }}>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>
            Student Learning Platform
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Welcome, {user?.name}!
          </Typography>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#ff4d4d',
              animation: 'pulse 2s infinite'
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const StudentAppContent = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, loading, signOut } = useAuth();

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'problem-solving':
        return <ProblemSolving />;
      case 'todo':
        return <TodoApp />;
      case 'notes':
        return <NotesApp />;
      case 'study-timer':
        return <StudyTimer />;
      case 'resources':
        return <ResourceLibrary />;
      default:
        return <Dashboard />;
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            background: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)'
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <CircularProgress sx={{ color: '#ff4d4d', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'white' }}>
              Loading Student Suite...
            </Typography>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          <Navigation 
            currentView={currentView}
            setCurrentView={setCurrentView}
            open={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            user={user}
            onSignOut={signOut}
          />
          <MainContent 
            component="main" 
            sx={{ 
              flexGrow: 1,
              marginLeft: sidebarOpen ? '280px' : 0,
              width: sidebarOpen ? 'calc(100% - 280px)' : '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <StudentHeader 
              title={getViewTitle(currentView)}
              onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
              user={user}
              onSignOut={signOut}
            />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              {renderCurrentView()}
            </Container>
            <Footer />
          </MainContent>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

function StudentApp() {
  return (
    <AuthProvider>
      <StudentAppContent />
    </AuthProvider>
  );
}

const getViewTitle = (view) => {
  const titles = {
    'dashboard': 'Learning Dashboard',
    'problem-solving': 'Problem Solving Hub',
    'todo': 'Task Manager',
    'notes': 'Study Notes',
    'study-timer': 'Focus Timer',
    'resources': 'Resource Library'
  };
  return titles[view] || 'Student App';
};

export default StudentApp;